import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { AzelRange, LOADING_CIRCLE, Status } from 'common/utils/constants';
import { generateHeatmapColorFromVal, azelToHeatmapIndex, getDefaultHeatmapRange } from 'common/utils/helpers';
import BeamRankDisplay from 'components/features/BeamRankDisplay/BeamRankDisplay';
import RasterHeatmapMetaDataBox from '../RasterHeatmapMetaDataBox/RasterHeatmapMetaDataBox';
import RasterHeatmapSlider from '../RasterHeatmapSlider/RasterHeatmapSlider';
import './RasterHistoryHeatmap.css';

class RasterHistoryHeatmap extends PureComponent {
  static defaultProps = {
    config: AzelRange,
    range: getDefaultHeatmapRange('ga1')
  };

  constructor(props) {
    super(props);
    this.crossCanvasRef = React.createRef();
    this.state = {
      height: null,
      width: null,
      heatmapDrawn: false,
      beamSelectDrawn: false,
      location: null,
      selectedBeamRank: 1
    };
  }

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  componentDidUpdate(prevProps) {
    const { heatmap, beamrank, range, selectedBeamRankIndex, selectedDeviceHeatmapRasterId } = this.props;
    const {
      range: prevRange,
      beamrank: prevBeamrank,
      selectedBeamRankIndex: prevSelectedBeamRankIndex,
      selectedDeviceHeatmapRasterId: prevSelectedDeviceHeatmapRasterId
    } = prevProps;
    const { heatmapDrawn } = this.state;

    if (!this.props.forViewAndExportHeatmap) {
      this.drawCrossCanvas();
    }

    if ((!heatmapDrawn && heatmap.values != null && this.heatmapCanvasRef) || beamrank !== prevBeamrank) {
      this.drawHeatmap();
      this.drawBeamSelect();
    }

    if (selectedBeamRankIndex !== prevSelectedBeamRankIndex) {
      this.drawBeamSelect();
    }

    if (heatmapDrawn && (range.min !== prevRange.min || range.max !== prevRange.max)) {
      this.drawHeatmap();
    } else if (selectedDeviceHeatmapRasterId) {
      if (
        selectedDeviceHeatmapRasterId !== prevSelectedDeviceHeatmapRasterId ||
        range.min !== prevRange.min ||
        range.max !== prevRange.max
      ) {
        this.drawHeatmap();
      }
    }
  }

  onResize = () => {
    let width = Math.min(this.heatmapDivRef.clientWidth, this.heatmapDivRef.clientWidth); //width will most likely be limiting factor on small screens
    let height =
      this.props.forViewAndExportHeatmap && this.props.isGa2 ? width : Math.round(width / this.props.config.ratio);
    this.setState({ height: height, width: width, heatmapDrawn: false, beamSelectDrawn: false });
  };

  drawBeamSelect() {
    const { config, selectedDeviceHeatmapRecord } = this.props;
    if (this.num_steps_x == null) {
      this.calculateSizes();
    }
    let targets = [];
    let arcs = [];
    let cross = [];

    let beamRankTable = this.props.beamrank ?? [];

    for (let i = 0; i < beamRankTable.length; i++) {
      /* do same calculation as we do when we build the heatmap array, to match azel in beamrank table to index in heatmap array*/
      let result = azelToHeatmapIndex(beamRankTable[i].az, beamRankTable[i].el, config);
      targets.push(result);
    }

    let active = azelToHeatmapIndex(0, 0, config);
    let width = this.state.width;
    let height = this.state.height;
    let ctx = this.beamSelectCanvasRef?.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    let num_steps_x = config.az.num;
    let num_steps_y = config.el.num;
    let box_width = width / num_steps_x;
    let box_height = height / num_steps_y;

    for (let i = 0; i < targets.length; i++) {
        let x = Math.round(width - box_width * (targets[i][1] + 1));
        let y = Math.round(height - (targets[i][0] + 1) * box_height);
        arcs.push([x + box_width / 2, y + box_height / 2, Math.max(box_width, box_height) / 2]);
    }

    let x = Math.round(width - box_width * (active[1] + 1));
    let y = Math.round(height - (active[0] + 1) * box_height);
    let overshoot = Math.max(box_width, box_height) / 2;

    if (!this.props.forViewAndExportHeatmap) {
        cross = [
            x - overshoot,
            y + box_height / 2,
            x + box_width + overshoot,
            y + box_height / 2,
            x + box_width / 2,
            y - overshoot,
            x + box_width / 2,
            y + box_height + overshoot
        ];
        if (cross.length === 8) {
            ctx.beginPath();
            ctx.moveTo(cross[0] - 5, cross[1] - 7);
            ctx.lineTo(cross[2], cross[3] - 7);
            ctx.lineWidth = 2;
            ctx.strokeStyle = 'black';
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cross[4] - 3, cross[5] - 7);
            ctx.lineTo(cross[6] - 3, cross[7] - 7);
            ctx.stroke();
        }
    }

    // **Draw Circles and Numbers FIRST**
    for (let i = 0; i < arcs.length; i++) {
        let circleX = arcs[i][0] - 3;
        let circleY = arcs[i][1] - 6;
        let radius = arcs[i][2];

        ctx.beginPath();
        ctx.arc(circleX, circleY, radius, 0, 2 * Math.PI);
        ctx.lineWidth = 2;

        if (!this.props.forViewAndExportHeatmap) {
            ctx.moveTo(circleX, circleY + 8);
            ctx.lineTo(circleX, circleY + 12);
            ctx.moveTo(circleX, circleY - 8);
            ctx.lineTo(circleX, circleY - 12);
            ctx.moveTo(circleX + 8, circleY);
            ctx.lineTo(circleX + 12, circleY);
            ctx.moveTo(circleX - 8, circleY);
            ctx.lineTo(circleX - 12, circleY);
        }
        
        ctx.strokeStyle = this.props.selectedBeamRankIndex === i + 1 && !this.props.hidePopover ? '#000000' : '#FFFFFF';
        ctx.fillStyle = this.props.selectedBeamRankIndex === i + 1 && !this.props.hidePopover ? '#000000' : '#FFFFFF';
        ctx.stroke();

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = !this.props.forViewAndExportHeatmap ? 'bold 10px Verdana' : 'bold 14px Verdana';

        ctx.fillText(i === 3 ? 'C' : `${i + 1}`, (arcs[i][0] + arcs[i][0]) / 2 - 3, (arcs[i][1] + arcs[i][1]) / 2 - 6);
    }

    // **Draw Popover LAST (So it Appears on Top)**
    for (let i = 0; i < targets.length; i++) {
        let x = Math.round(width - box_width * (targets[i][1] + 1));
        let y = Math.round(height - (targets[i][0] + 1) * box_height);

        if (this.props.selectedBeamRankIndex - 1 === i && !this.props.hidePopover) {
            this.drawPopOverRectangle(
                ctx,
                x + 20,
                y + 10,
                215,
                55,
                5,
                i + 1,
                beamRankTable[i],
                selectedDeviceHeatmapRecord
            );
        }
    }

    this.setState({ beamSelectDrawn: true });
}

  drawHeatmap() {
    const { config, range, heatmap } = this.props;

    if (this.num_steps_x == null) {
      this.calculateSizes();
    }
    let width = this.state.width;
    let height = this.state.height;

    let ctx = this.heatmapCanvasRef?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);

    let num_steps_x = config.az.num;
    let num_steps_y = config.el.num;
    let box_width = width / num_steps_x;
    let box_height = height / num_steps_y;

    let val = 0;
    let color;

    let x = 0;
    let y = height - box_height;
    let { min, max } = range;

    if (!heatmap?.values?.length) return;

    for (let i = 0; i < num_steps_y; i++) {
      x = width - box_width;
      for (let j = 0; j < num_steps_x; j++) {
        val = heatmap.values[i][j];
        color = generateHeatmapColorFromVal(val, max, min);
        ctx.fillStyle = this.props.placeholder
          ? 'lightgrey'
          : 'rgb(' + color.red + ', ' + color.green + ', ' + color.blue + ')';

        // draw each grid background color
        ctx.fillRect(Math.round(x), Math.round(y), Math.ceil(box_width), Math.ceil(box_height));
        //draw grid border line
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 0.2;
        ctx.strokeRect(x, y, box_width, box_height);

        x -= box_width;
      }
      y -= box_height;
    }

    this.setState({ heatmapDrawn: true });
  }

  //draw the rounded-edge rectangles such as [-1, 10]
  drawRoundedRectangle(ctx, x, y, w, h, r, az, el) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.beginPath();
    ctx.moveTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y - r + h);
    ctx.lineTo(x, y + r);
    ctx.fill();

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '15px Arial';
    ctx.fillText(
      '' + -az.toFixed(0).toString() + ', ' + el.toFixed(0).toString(),
      (x + x + w) / 2,
      (y + y + h) / 2 + 1
    );
  }

  drawPopOverRectangle(ctx, x, y, w, h, r, rank, beamRank, selectedHeatMap) {
    let azVal = beamRank.az ?? 'N/A';
    let elVal = beamRank.el ?? 'N/A';

    const key = `${azVal},${elVal}`;
    let power = Math.floor(selectedHeatMap?.heatmap?.[key]);

    if (rank === 4) {
      rank = 'C (Custom)';
      power = 'N/A';
    }

    // (0, 0), (0, -y), (-x, 0 ), (+x, 0), (0, +y)
    if (azVal === 0 && elVal === 0) {
      x -= r * 2;
      y += r * 2;
    } else if (azVal === 0 && elVal < 0) {
      x -= 10;
      y -= h + r * 4;
    } else if (azVal < 0 && elVal === 0) {
      x -= w + 35;
    } else if (azVal > 0 && elVal === 0) {
      // x -= 45;
      // y += 0;
    } else if (azVal === 0 && elVal > 0) {
      x -= r * 2;
      y += r * 2;
    }
    // (-x, -y)
    if (azVal < 0 && elVal < 0) {
      x -= w + 35;
      y -= h;
    }
    // (-x, +y)
    if (azVal < 0 && elVal > 0) {
      x -= w + 35;
      y += 0;
    }
    // +x, +y
    if (azVal > 0 && elVal > 0) {
      x -= 0;
      y += 0;
    }
    // (+x, -y)
    if (azVal > 0 && elVal < 0) {
      x += 0;
      y -= h;
    }

    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.moveTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y - r + h);
    ctx.lineTo(x, y + r);
    ctx.fill();

    // Draw the text on the card
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'left';
    ctx.strokeStyle = 'grey';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.textBaseline = 'top';
    ctx.font = '13px Rubik';

    const txt0 = '   ';
    const txt1 = `     Rank ${rank}`;
    const txt2 = '   ';
    const txt3 = `     Az/EL  ${azVal.toString()}, ${elVal.toString()}         Power  ${power}`;
    const lineHeight = 10;
    const lines = [txt0, txt1, txt2, txt3];

    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], x, y + i * lineHeight);
    }
  }

  drawCrossCanvas() {
    const { config } = this.props;
    if (this.state.location == null) {
      return;
    }

    let ctx = this.crossCanvasRef.current?.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.state.width, this.state.height);
    ctx.strokeStyle = 'black';
    ctx.beginPath();
    ctx.moveTo(0, this.state.location.y);
    ctx.lineTo(this.calcWidth, this.state.location.y);
    ctx.stroke();
    ctx.stroke(); //double stroke, had trouble with slight transparency otherwise

    ctx.beginPath();
    ctx.moveTo(this.state.location.x, 0);
    ctx.lineTo(this.state.location.x, this.calcHeight);
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.stroke();

    let az = config.az.min + (this.state.location.x / this.box_width) * config.az.stepSize;
    let el = config.el.max - (this.state.location.y / this.box_height) * config.el.stepSize;
    az = Math.round(az);
    el = Math.round(el);

    let x, y;
    if (az >= 0) {
      x = this.state.location.x - 70;
    } else {
      x = this.state.location.x + 5;
    }
    if (el <= 0) {
      y = this.state.location.y - 30;
    } else {
      y = this.state.location.y + 5;
    }
    this.drawRoundedRectangle(ctx, x, y, 65, 25, 5, az, el);
  }

  calculateSizes() {
    const { config } = this.props;

    this.num_steps_x = config.az.num;
    this.num_steps_y = config.el.num;

    this.box_width = this.state.width / this.num_steps_x;
    this.box_height = this.state.height / this.num_steps_y;

    this.calcHeight = this.num_steps_y * this.box_height;
    this.calcWidth = this.num_steps_x * this.box_width;
  }

  render() {
    const {
      id,
      getRasterRecordsForDeviceStatus,
      getRasterRecordStatus,
      heatmap,
      beamrank,
      range,
      forViewAndExportHeatmap,
      forMaintHeatmap,
      showLegend,
      selectedDeviceHeatmapRecord,
      placeholder
    } = this.props;
    let overlay = null;

    if (getRasterRecordsForDeviceStatus === Status.FAILURE || getRasterRecordStatus === Status.FAILURE) {
      overlay = (
        <div id="HeatmapViewAndExportModal_ErrorOverlay" className="HeatmapViewAndExportModal_ErrorOverlay">
          <div id="Global_Table_Error_Div" className="Global_Table_Error_Div">
            <div id="Global_Table_ErrorOverlay_Title" className="Global_Table_ErrorOverlay_Title">
              Error{' '}
            </div>
            <div id="Global_Table_ErrorOverlay_Message" className="Global_Table_ErrorOverlay_Message">
              An unexpected problem occurred and this data is unavailable. Please try refreshing the page, and if the
              problem persists, contact us at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          </div>
        </div>
      );
    } else if (getRasterRecordsForDeviceStatus === Status.PENDING || getRasterRecordStatus === Status.PENDING) {
      overlay = (
        <div id="HeatmapViewAndExportModal_HeatMapDiv_Loading" className="HeatmapViewAndExportModal_HeatMapDiv_Loading">
          {LOADING_CIRCLE}
        </div>
      );
    } else if (getRasterRecordsForDeviceStatus === Status.NONE && heatmap?.values?.length === 0) {
      overlay = (
        <div id="HeatmapViewAndExportModal_HeatMapDiv_NoData" className="HeatmapViewAndExportModal_HeatMapDiv_NoData">
          There is no records for this devices.
        </div>
      );
    } else if (heatmap?.values?.length === 0 && getRasterRecordStatus === Status.SUCCESS) {
      overlay = (
        <div id="HeatmapViewAndExportModal_HeatMapDiv_NoData" className="HeatmapViewAndExportModal_HeatMapDiv_NoData">
          There is no heatmap data for this record.
        </div>
      );
    }

    return (
      <div className="RasterHistoryHeatmap" id="RasterHistoryHeatmap">
        <div
          id="RasterHistoryHeatmap_Heatmap_Div"
          className="RasterHistoryHeatmap_Heatmap_Div"
          ref={(ref) => (this.heatmapDivRef = ref)}
          style={{ height: this.state.height != null ? this.state.height + 'px' : 'auto' }}
        >
          {overlay}
          {this.state.width && heatmap && heatmap?.values && (
            <div id="RasterHistoryHeatmap_Heatmap_Inner_Div" className="RasterHistoryHeatmap_Heatmap_Inner_Div">
              <canvas
                ref={(ref) => (this.heatmapCanvasRef = ref)}
                height={this.state.height}
                width={this.state.width}
              />
              <canvas
                id={'beamSelectCanvasRef'}
                ref={(ref) => (this.beamSelectCanvasRef = ref)}
                height={this.state.height}
                width={this.state.width}
                onMouseMove={(event) => {
                  let x = event.nativeEvent.offsetX;
                  let y = event.nativeEvent.offsetY;
                  if (
                    this.state.location == null ||
                    Math.abs(this.state.location.x - x) > 2 ||
                    Math.abs(this.state.location.y - y) > 2
                  ) {
                    if (x < this.calcWidth && y < this.calcHeight) {
                      this.setState({ location: { x: x, y: y } });
                    }
                  }
                }}
              />
              <canvas
                height={this.state.height}
                width={this.state.width}
                ref={this.crossCanvasRef}
                onMouseMove={(event) => {
                  let x = event.nativeEvent.offsetX;
                  let y = event.nativeEvent.offsetY;
                  if (
                    this.state.location == null ||
                    Math.abs(this.state.location.x - x) > 2 ||
                    Math.abs(this.state.location.y - y) > 2
                  ) {
                    if (x < this.calcWidth && y < this.calcHeight) {
                      this.setState({ location: { x: x, y: y } });
                    }
                  }
                }}
              />
            </div>
          )}
        </div>
        {forViewAndExportHeatmap && (
          <div id="RasterHistoryHeatmap_Metrics_Div" className="RasterHistoryHeatmap_Metrics_Div">
            <RasterHeatmapMetaDataBox
              deviceId={id}
              beamrank={beamrank}
              selectedDeviceHeatmapRecord={selectedDeviceHeatmapRecord}
              hidePopover={Boolean(beamrank)}
              placeholder={placeholder}
              forViewAndExportHeatmap={true}
              forMaintHeatmap={forMaintHeatmap}
            />
          </div>
        )}
        {showLegend && (
          <RasterHeatmapSlider
            value={range}
            min={heatmap.min}
            max={heatmap.max}
            hideRange={true}
            heatmap={heatmap}
            forViewAndExportHeatmap={true}
          />
        )}
        {!forViewAndExportHeatmap && (
          <div
            id="RasterHistoryHeatmap_BeamRank_Table_Container"
            className="RasterHistoryHeatmap_BeamRank_Table_Container"
          >
            <div
              id="RasterHistoryHeatmap_BeamRank_Table_InnerContainer"
              className="RasterHistoryHeatmap_BeamRank_Table_InnerContainer"
            >
              {beamrank ? <BeamRankDisplay {...this.props} parent={this} /> : LOADING_CIRCLE}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default connect((store) => {
  let selectedRasterId = store.ui.singleDevicePage?.heatmap?.selectedRasterId;
  let getRasterRecordStatusName = selectedRasterId && `GET-RasterRecord-${selectedRasterId}`;

  return {
    azimuth: store.data.heatmap?.activeBeam?.azimuth,
    elevation: store.data.heatmap?.activeBeam?.elevation,
    getRasterRecordStatus: getRasterRecordStatusName ? store.statuses[getRasterRecordStatusName] : 0,
    selectedBeamRankIndex: store.ui.singleDevicePage?.heatmap?.selectedBeamRankIndex
  };
})(RasterHistoryHeatmap);
